import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [petals, setPetals] = useState<JSX.Element[]>([]);

  useEffect(() => {
    const newPetals = Array.from({ length: 20 }, (_, i) => (
      <div
        key={i}
        className="petal"
        style={{
          left: `${Math.random() * 100}%`,
          width: `${8 + Math.random() * 8}px`,
          height: `${8 + Math.random() * 8}px`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${8 + Math.random() * 8}s`,
        }}
      />
    ));
    setPetals(newPetals);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #2d1b4e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background */}
      <Box className="sakura-petals">{petals}</Box>
      
      {/* Background Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(233, 30, 99, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'hero-glow 10s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-20%',
          left: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156, 39, 176, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'hero-glow 12s ease-in-out infinite reverse',
        }}
      />

      {/* Back Button */}
      <IconButton
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          color: 'rgba(255, 255, 255, 0.7)',
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
          },
          zIndex: 2,
        }}
      >
        <ArrowBack />
      </IconButton>

      <Container maxWidth="sm">
        <Paper
          elevation={0}
          className="glass-card glass-card-hover"
          sx={{
            p: { xs: 4, sm: 6 },
            borderRadius: 4,
            position: 'relative',
            zIndex: 1,
            animation: 'fadeIn 0.6s ease-out',
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                animation: 'float 6s ease-in-out infinite',
              }}
            >
              <Logo size={72} />
            </Box>
          </Box>
          
          {/* Title */}
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #E91E63, #F48FB1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="rgba(255, 255, 255, 0.6)"
            sx={{ mb: 5 }}
          >
            Sign in to continue to Sakura ShiftSync
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                bgcolor: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                color: '#ef9a9a',
                animation: 'fadeIn 0.3s ease-out',
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="sakura-input"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover fieldset': {
                    borderColor: '#E91E63',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E91E63',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#E91E63',
                },
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="sakura-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover fieldset': {
                    borderColor: '#E91E63',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E91E63',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#E91E63',
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  color: '#F48FB1',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: '#E91E63',
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              className="sakura-button"
              sx={{
                py: 1.8,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #E91E63 0%, #9C27B0 100%)',
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #C2185B 0%, #7B1FA2 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(233, 30, 99, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'white',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Divider */}
          <Box sx={{ my: 4, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            <Typography
              variant="caption"
              sx={{ px: 2, color: 'rgba(255, 255, 255, 0.4)' }}
            >
              or continue with
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
          </Box>

          {/* Social Login Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              sx={{
                py: 1.5,
                borderRadius: 3,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Google
            </Button>
            <Button
              fullWidth
              variant="outlined"
              sx={{
                py: 1.5,
                borderRadius: 3,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Microsoft
            </Button>
          </Box>

          {/* Sign Up Link */}
          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 4, color: 'rgba(255, 255, 255, 0.5)' }}
          >
            Don't have an account?{' '}
            <Link
              component={RouterLink}
              to="/register"
              sx={{
                color: '#E91E63',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Contact Sales
            </Link>
          </Typography>
        </Paper>

        {/* Footer Text */}
        <Typography
          variant="caption"
          align="center"
          sx={{
            display: 'block',
            mt: 4,
            color: 'rgba(255, 255, 255, 0.3)',
          }}
        >
          Protected by enterprise-grade security
        </Typography>
      </Container>
    </Box>
  );
};

export default LoginPage;
